/* oxlint-disable jsx-a11y/prefer-tag-over-role -- The Toolbar API needs a neutral element with explicit group semantics. */
import * as React from 'react'
import * as ToolbarPrimitive from '@radix-ui/react-toolbar'
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { clsx } from 'clsx'

import { useControllableState } from '../../hooks/useControllableState'
import styles from './Toolbar.module.css'

type ToolbarElement = React.ElementRef<typeof ToolbarPrimitive.Root>
type ToolbarProps = ToolbarPrimitive.ToolbarProps
type ToolbarContextValue = Pick<ToolbarProps, 'dir' | 'orientation'>

const ToolbarContext = React.createContext<ToolbarContextValue>({
  orientation: 'horizontal',
})

const ToolbarRoot = React.forwardRef<ToolbarElement, ToolbarProps>(
  ({ className, dir, orientation = 'horizontal', ...props }, forwardedRef) => {
    const contextValue = React.useMemo(
      () => ({ dir, orientation }),
      [dir, orientation],
    )

    return (
      <ToolbarContext.Provider value={contextValue}>
        <ToolbarPrimitive.Root
          {...props}
          ref={forwardedRef}
          className={clsx(styles.root, className)}
          dir={dir}
          orientation={orientation}
        />
      </ToolbarContext.Provider>
    )
  },
)

ToolbarRoot.displayName = 'Toolbar'

type ToolbarGroupElement = HTMLDivElement
type ToolbarGroupProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'role'>

const ToolbarGroup = React.forwardRef<ToolbarGroupElement, ToolbarGroupProps>(
  ({ className, ...props }, forwardedRef) => (
    <div
      {...props}
      ref={forwardedRef}
      role="group"
      className={clsx(styles.group, className)}
    />
  ),
)

ToolbarGroup.displayName = 'Toolbar.Group'

type ToolbarButtonElement = React.ElementRef<typeof ToolbarPrimitive.Button>
type ToolbarButtonProps = ToolbarPrimitive.ToolbarButtonProps

const ToolbarButton = React.forwardRef<
  ToolbarButtonElement,
  ToolbarButtonProps
>(({ className, ...props }, forwardedRef) => (
  <ToolbarPrimitive.Button
    {...props}
    ref={forwardedRef}
    className={clsx(styles.action, className)}
  />
))

ToolbarButton.displayName = 'Toolbar.Button'

type ToolbarLinkElement = React.ElementRef<typeof ToolbarPrimitive.Link>
type ToolbarLinkProps = ToolbarPrimitive.ToolbarLinkProps

const ToolbarLink = React.forwardRef<ToolbarLinkElement, ToolbarLinkProps>(
  ({ className, ...props }, forwardedRef) => (
    <ToolbarPrimitive.Link
      {...props}
      ref={forwardedRef}
      className={clsx(styles.action, className)}
    />
  ),
)

ToolbarLink.displayName = 'Toolbar.Link'

type ToolbarToggleGroupElement = React.ElementRef<
  typeof ToolbarPrimitive.ToggleGroup
>
type ToolbarToggleGroupSingleProps =
  ToolbarPrimitive.ToolbarToggleGroupSingleProps
type ToolbarToggleGroupMultipleProps =
  ToolbarPrimitive.ToolbarToggleGroupMultipleProps
type ToolbarToggleGroupProps =
  | ToolbarToggleGroupSingleProps
  | ToolbarToggleGroupMultipleProps
const ToolbarToggleGroupDisabledContext = React.createContext(false)

const ToolbarToggleGroupSingle = React.forwardRef<
  ToolbarToggleGroupElement,
  ToolbarToggleGroupSingleProps
>(
  (
    { className, value: valueProp, defaultValue, onValueChange, ...props },
    forwardedRef,
  ) => {
    const { dir, orientation } = React.useContext(ToolbarContext)
    const disabled = props.disabled ?? false
    const [value, setValue] = useControllableState<string>({
      prop: valueProp,
      defaultProp: defaultValue,
      onChange: onValueChange,
    })
    const handleValueChange = React.useCallback(
      (nextValue: string) => {
        if (nextValue) setValue(nextValue)
      },
      [setValue],
    )

    return (
      <ToolbarToggleGroupDisabledContext.Provider value={disabled}>
        <ToggleGroupPrimitive.Root
          data-orientation={orientation}
          dir={dir}
          {...props}
          ref={forwardedRef}
          className={clsx(styles.toggleGroup, className)}
          disabled={disabled}
          type="single"
          value={value ?? ''}
          rovingFocus={false}
          onValueChange={handleValueChange}
        />
      </ToolbarToggleGroupDisabledContext.Provider>
    )
  },
)

ToolbarToggleGroupSingle.displayName = 'Toolbar.ToggleGroupSingle'

const ToolbarToggleGroupMultiple = React.forwardRef<
  ToolbarToggleGroupElement,
  ToolbarToggleGroupMultipleProps
>(({ className, ...props }, forwardedRef) => {
  const { dir, orientation } = React.useContext(ToolbarContext)
  const disabled = props.disabled ?? false

  return (
    <ToolbarToggleGroupDisabledContext.Provider value={disabled}>
      <ToggleGroupPrimitive.Root
        data-orientation={orientation}
        dir={dir}
        {...props}
        ref={forwardedRef}
        className={clsx(styles.toggleGroup, className)}
        disabled={disabled}
        rovingFocus={false}
      />
    </ToolbarToggleGroupDisabledContext.Provider>
  )
})

ToolbarToggleGroupMultiple.displayName = 'Toolbar.ToggleGroupMultiple'

const ToolbarToggleGroup = React.forwardRef<
  ToolbarToggleGroupElement,
  ToolbarToggleGroupProps
>((props, forwardedRef) => {
  if (props.type === 'single') {
    return <ToolbarToggleGroupSingle {...props} ref={forwardedRef} />
  }

  return <ToolbarToggleGroupMultiple {...props} ref={forwardedRef} />
})

ToolbarToggleGroup.displayName = 'Toolbar.ToggleGroup'

type ToolbarToggleItemElement = React.ElementRef<
  typeof ToolbarPrimitive.ToggleItem
>
type ToolbarToggleItemProps = ToolbarPrimitive.ToolbarToggleItemProps

const ToolbarToggleItem = React.forwardRef<
  ToolbarToggleItemElement,
  ToolbarToggleItemProps
>(({ className, disabled = false, ...props }, forwardedRef) => {
  const groupDisabled = React.useContext(ToolbarToggleGroupDisabledContext)
  const isDisabled = groupDisabled || disabled

  return (
    <ToolbarPrimitive.Button asChild disabled={isDisabled}>
      <ToggleGroupPrimitive.Item
        {...props}
        ref={forwardedRef}
        className={clsx(styles.action, className)}
        disabled={isDisabled}
      />
    </ToolbarPrimitive.Button>
  )
})

ToolbarToggleItem.displayName = 'Toolbar.ToggleItem'

type ToolbarSeparatorElement = React.ElementRef<
  typeof ToolbarPrimitive.Separator
>
type ToolbarSeparatorProps = ToolbarPrimitive.ToolbarSeparatorProps

const ToolbarSeparator = React.forwardRef<
  ToolbarSeparatorElement,
  ToolbarSeparatorProps
>(({ className, decorative = true, ...props }, forwardedRef) => (
  <ToolbarPrimitive.Separator
    {...props}
    ref={forwardedRef}
    decorative={decorative}
    className={clsx(styles.separator, className)}
  />
))

ToolbarSeparator.displayName = 'Toolbar.Separator'

const Toolbar = Object.assign(ToolbarRoot, {
  Group: ToolbarGroup,
  Button: ToolbarButton,
  Link: ToolbarLink,
  ToggleGroup: ToolbarToggleGroup,
  ToggleItem: ToolbarToggleItem,
  Separator: ToolbarSeparator,
})

export { Toolbar }
export type {
  ToolbarProps,
  ToolbarGroupProps,
  ToolbarButtonProps,
  ToolbarLinkProps,
  ToolbarToggleGroupProps,
  ToolbarToggleGroupSingleProps,
  ToolbarToggleGroupMultipleProps,
  ToolbarToggleItemProps,
  ToolbarSeparatorProps,
}
